import {InstallEnvironment, InstallOs, IN_APP_NAMES} from './installEnvironment'

/** The icons the steps draw inline, as the browser shows them (InstallGuideIcon draws them). */
export type GuideIcon = 'share' | 'add-square' | 'kebab' | 'dots' | 'menu' | 'install'

/**
 * A piece of a step: plain text, a browser's own label (Persian as the browser shows it in
 * Persian, the English one beside it for browsers set to English), or an icon.
 */
export type GuidePart = string | {ui: string; en?: string} | {icon: GuideIcon}
export type GuideStep = GuidePart[]

/**
 * prompt: the browser installs with one click from the page (beforeinstallprompt), the steps are
 * the way round; manual: only the browser's own menu installs; other-browser: this browser cannot
 * install it here; open-in-browser: an app's built-in browser, which has to hand the page over.
 */
export type InstallSupport = 'prompt' | 'manual' | 'other-browser' | 'open-in-browser'

export type InstallGuide = {
  id: string
  support: InstallSupport
  /** Shown above the steps when the user first has to move elsewhere. */
  message?: string
  steps: GuideStep[]
  notes?: GuideStep[]
  /** Where the installed app is found afterwards. */
  where: string
}

const ui = (fa: string, en?: string): GuidePart => (en ? {ui: fa, en} : {ui: fa})
const icon = (name: GuideIcon): GuidePart => ({icon: name})

const ADD_TO_HOME_IOS = ui('افزودن به صفحهٔ اصلی', 'Add to Home Screen')
const ADD_TO_HOME_ANDROID = ui('افزودن به صفحهٔ اصلی', 'Add to Home screen')
const SHARE = ui('اشتراک‌گذاری', 'Share')
const ADD = ui('افزودن', 'Add')
const INSTALL = ui('نصب', 'Install')

const DESKTOP_WHERE: Record<InstallOs | 'other', string> = {
  windows: 'منوی Start و نوار وظیفه',
  macos: 'Dock و Launchpad',
  linux: 'فهرست برنامه‌ها',
  chromeos: 'فهرست برنامه‌ها (Launcher)',
  ios: 'صفحهٔ اصلی آیفون',
  ipados: 'صفحهٔ اصلی آیپد',
  android: 'صفحهٔ اصلی و فهرست برنامه‌های گوشی',
  other: 'فهرست برنامه‌های دستگاه',
}

const appleWhere = (env: InstallEnvironment) =>
  DESKTOP_WHERE[env.os === 'ipados' ? 'ipados' : 'ios']

/** Safari on iPhone and iPad: the share sheet's «Add to Home Screen». */
const iosSafari = (env: InstallEnvironment): InstallGuide => {
  const ipad = env.os === 'ipados'
  return {
    id: 'ios-safari',
    support: 'manual',
    steps: [
      [
        ipad ? 'در بالای صفحه، کنار نوار آدرس، دکمهٔ ' : 'در پایین صفحه، دکمهٔ ',
        icon('share'),
        ' ',
        SHARE,
        ' را بزنید.',
      ],
      [
        'در فهرستی که باز می‌شود کمی پایین بروید و ',
        icon('add-square'),
        ' ',
        ADD_TO_HOME_IOS,
        ' را بزنید.',
      ],
      [
        'اگر گزینهٔ ',
        ui('باز کردن به‌عنوان برنامهٔ وب', 'Open as Web App'),
        ' دیده می‌شود روشن بماند؛ بعد ',
        ADD,
        ' را بزنید.',
      ],
    ],
    notes: [
      ...(ipad
        ? []
        : [['اگر دکمهٔ اشتراک‌گذاری را نمی‌بینید (iOS ۲۶)، اول ', icon('dots'), ' را بزنید.']]),
      [
        'اگر ',
        ADD_TO_HOME_IOS,
        ' در فهرست نیست، پایین فهرست ',
        ui('ویرایش کنش‌ها', 'Edit Actions'),
        ' را بزنید و آن را اضافه کنید.',
      ],
    ],
    where: appleWhere(env),
  }
}

/** Other iOS browsers can add to the home screen from iOS 16.4; before that only Safari could. */
const iosOtherBrowser = (env: InstallEnvironment): InstallGuide => {
  const where = appleWhere(env)
  if (env.osVersion !== undefined && env.osVersion < 17) {
    return {
      id: 'ios-old-browser',
      support: 'other-browser',
      message:
        'در این نسخهٔ iOS فقط Safari می‌تواند برنامه را به صفحهٔ اصلی اضافه کند. لینک پنل را کپی کنید، در Safari باز کنید و همین راهنما را آنجا دنبال کنید. (از iOS ۱۶٫۴ به بعد، مرورگرهای دیگر هم می‌توانند.)',
      steps: [],
      where,
    }
  }
  const finish: GuideStep = [ADD_TO_HOME_IOS, ' و بعد ', ADD, ' را بزنید.']
  switch (env.browser) {
    case 'chrome':
      return {
        id: 'ios-chrome',
        support: 'manual',
        steps: [
          ['در انتهای نوار آدرس (بالای صفحه)، دکمهٔ ', icon('share'), ' ', SHARE, ' را بزنید.'],
          [
            icon('add-square'),
            ' ',
            ADD_TO_HOME_IOS,
            ' را بزنید؛ اگر در فهرست نیست، اول ',
            ui('بیشتر', 'More'),
            ' را بزنید.',
          ],
          [ADD, ' را بزنید.'],
        ],
        where,
      }
    case 'edge':
      return {
        id: 'ios-edge',
        support: 'manual',
        steps: [
          ['دکمهٔ ', icon('menu'), ' پایین صفحه را بزنید.'],
          [icon('share'), ' ', SHARE, ' را بزنید.'],
          finish,
        ],
        where,
      }
    case 'firefox':
      return {
        id: 'ios-firefox',
        support: 'manual',
        steps: [
          ['دکمهٔ ', icon('menu'), ' را بزنید.'],
          [icon('share'), ' ', SHARE, ' را بزنید.'],
          finish,
        ],
        where,
      }
    default:
      return {
        id: 'ios-other',
        support: 'manual',
        steps: [
          ['دکمهٔ ', icon('share'), ' ', SHARE, ' مرورگر را بزنید (در نوار آدرس یا منوی مرورگر).'],
          finish,
        ],
        notes: [['اگر این گزینه نبود، پنل را در Safari باز کنید.']],
        where,
      }
  }
}

/** An app's built-in browser (Instagram, Telegram, …) installs nothing: open the page outside. */
const inAppBrowser = (env: InstallEnvironment): InstallGuide => {
  const apple = env.os === 'ios' || env.os === 'ipados'
  const host = IN_APP_NAMES[env.inApp || 'webview']
  return {
    id: apple ? 'ios-in-app' : 'android-in-app',
    support: 'open-in-browser',
    message: `این صفحه داخل ${host} باز شده و از اینجا نصب نمی‌شود. اول آن را در ${
      apple ? 'Safari' : 'Chrome'
    } باز کنید.`,
    steps: apple
      ? [
          ['دکمهٔ ', icon('dots'), ' را بزنید (بالا یا پایین صفحه).'],
          [
            ui('باز کردن در Safari', 'Open in Safari'),
            ' یا ',
            ui('باز کردن در مرورگر', 'Open in browser'),
            ' را بزنید.',
          ],
          ['در Safari همین راهنما را ادامه دهید.'],
        ]
      : [
          ['دکمهٔ ', icon('kebab'), ' را بزنید (معمولاً بالای صفحه).'],
          [
            ui('باز کردن در Chrome', 'Open in Chrome'),
            ' یا ',
            ui('باز کردن در مرورگر', 'Open in browser'),
            ' را بزنید.',
          ],
          ['در مرورگر همین راهنما را ادامه دهید.'],
        ],
    notes: [['اگر این گزینه نبود، لینک را با دکمهٔ زیر کپی کنید و در مرورگر باز کنید.']],
    where: DESKTOP_WHERE[env.os],
  }
}

const android = (env: InstallEnvironment): InstallGuide => {
  const where = DESKTOP_WHERE.android
  switch (env.browser) {
    case 'chrome':
      return {
        id: 'android-chrome',
        support: 'prompt',
        steps: [
          ['دکمهٔ ', icon('kebab'), ' بالای صفحه، کنار نوار آدرس، را بزنید.'],
          [ui('نصب برنامه', 'Install app'), ' یا ', ADD_TO_HOME_ANDROID, ' را بزنید.'],
          [INSTALL, ' را تأیید کنید.'],
        ],
        where,
      }
    case 'samsung':
      return {
        id: 'android-samsung',
        support: 'prompt',
        steps: [
          ['دکمهٔ ', icon('menu'), ' پایین صفحه را بزنید.'],
          [
            ui('افزودن صفحه به', 'Add page to'),
            ' و بعد ',
            ui('صفحهٔ اصلی', 'Home screen'),
            ' را بزنید.',
          ],
          [ADD, ' را بزنید.'],
        ],
        notes: [
          [
            'اگر در نوار آدرس نماد ',
            icon('install'),
            ' دیده می‌شود، زدن آن هم برنامه را نصب می‌کند.',
          ],
        ],
        where,
      }
    case 'firefox':
      return {
        id: 'android-firefox',
        support: 'manual',
        steps: [
          ['دکمهٔ ', icon('kebab'), ' کنار نوار آدرس را بزنید.'],
          [INSTALL, ' یا ', ADD_TO_HOME_ANDROID, ' را بزنید.'],
          [ADD, ' را تأیید کنید.'],
        ],
        where,
      }
    case 'edge':
      return {
        id: 'android-edge',
        support: 'prompt',
        steps: [
          ['دکمهٔ ', icon('dots'), ' پایین صفحه را بزنید.'],
          [ui('افزودن به گوشی', 'Add to phone'), ' را بزنید.'],
          [INSTALL, ' را تأیید کنید.'],
        ],
        where,
      }
    case 'opera':
      return {
        id: 'android-opera',
        support: 'prompt',
        steps: [
          ['دکمهٔ ', icon('kebab'), ' را بزنید.'],
          [ui('افزودن به…', 'Add to…'), ' و بعد ', ui('صفحهٔ اصلی', 'Home screen'), ' را بزنید.'],
          [ADD, ' را بزنید.'],
        ],
        where,
      }
    default:
      return {
        id: 'android-other',
        support: env.browser === 'brave' || env.browser === 'yandex' ? 'prompt' : 'manual',
        steps: [
          ['منوی مرورگر (', icon('kebab'), ' یا ', icon('menu'), ') را باز کنید.'],
          [ui('نصب برنامه', 'Install app'), ' یا ', ADD_TO_HOME_ANDROID, ' را بزنید.'],
        ],
        notes: [['اگر این گزینه نبود، پنل را در Chrome باز کنید.']],
        where,
      }
  }
}

const desktop = (env: InstallEnvironment): InstallGuide => {
  const where = DESKTOP_WHERE[env.os]
  switch (env.browser) {
    case 'chrome':
      return {
        id: 'desktop-chrome',
        support: 'prompt',
        steps: [
          ['در انتهای نوار آدرس، نماد ', icon('install'), ' ', INSTALL, ' را بزنید.'],
          [
            'اگر این نماد نیست: ',
            icon('kebab'),
            ' ← ',
            ui('ارسال، ذخیره و اشتراک‌گذاری', 'Cast, save, and share'),
            ' ← ',
            ui('نصب صفحه به‌عنوان برنامه', 'Install page as app'),
            '.',
          ],
          [INSTALL, ' را تأیید کنید.'],
        ],
        where,
      }
    case 'edge':
      return {
        id: 'desktop-edge',
        support: 'prompt',
        steps: [
          [
            'در نوار آدرس، نماد ',
            icon('install'),
            ' ',
            ui('برنامه در دسترس است', 'App available'),
            ' را بزنید.',
          ],
          [
            'اگر این نماد نیست: ',
            icon('dots'),
            ' ← ',
            ui('برنامه‌ها', 'Apps'),
            ' ← ',
            ui('نصب این سایت به‌عنوان برنامه', 'Install this site as an app'),
            '.',
          ],
          [INSTALL, ' را تأیید کنید.'],
        ],
        where,
      }
    case 'safari':
      if (env.os === 'macos' && env.browserVersion !== undefined && env.browserVersion < 17) {
        return {
          id: 'mac-safari-old',
          support: 'other-browser',
          message:
            'Safari از نسخهٔ ۱۷ (macOS Sonoma) به بعد برنامه را به Dock اضافه می‌کند. Safari را به‌روز کنید یا پنل را در Chrome یا Edge باز کنید.',
          steps: [],
          where,
        }
      }
      return {
        id: 'mac-safari',
        support: 'manual',
        steps: [
          [
            'در نوار منوی بالای صفحه ',
            ui('File'),
            ' ← ',
            ui('Add to Dock…'),
            ' را بزنید (یا دکمهٔ ',
            icon('share'),
            ' در نوار ابزار Safari ← ',
            ui('Add to Dock'),
            ').',
          ],
          [ui('Add'), ' را بزنید.'],
        ],
        where,
      }
    case 'firefox':
      return {
        id: 'desktop-firefox',
        support: 'other-browser',
        message:
          'Firefox روی رایانه نصب برنامه‌های وب را پشتیبانی نمی‌کند. پنل را در Chrome یا Edge باز کنید و همین راهنما را آنجا دنبال کنید.',
        steps: [],
        where,
      }
    default:
      return {
        id: 'desktop-other',
        support: env.browser === 'other' ? 'manual' : 'prompt',
        steps: [
          ['در نوار آدرس نماد ', icon('install'), ' ', INSTALL, ' را بزنید.'],
          ['یا از منوی مرورگر گزینهٔ ', INSTALL, ' را بزنید.'],
        ],
        notes: [['اگر این گزینه نبود، پنل را در Chrome یا Edge باز کنید.']],
        where,
      }
  }
}

/** The way to install the panel in this environment. */
export const installGuideFor = (env: InstallEnvironment): InstallGuide => {
  if (env.inApp) return inAppBrowser(env)
  if (env.os === 'ios' || env.os === 'ipados')
    return env.browser === 'safari' ? iosSafari(env) : iosOtherBrowser(env)
  if (env.os === 'android') return android(env)
  return desktop(env)
}

/** The devices and browsers the guide offers when the detected one is not the user's. */
export const GUIDE_CHOICES: {id: string; label: string; env: InstallEnvironment}[] = [
  {id: 'iphone-safari', label: 'آیفون · Safari', env: {os: 'ios', browser: 'safari', mobile: true}},
  {id: 'iphone-chrome', label: 'آیفون · Chrome', env: {os: 'ios', browser: 'chrome', mobile: true}},
  {id: 'iphone-edge', label: 'آیفون · Edge', env: {os: 'ios', browser: 'edge', mobile: true}},
  {
    id: 'iphone-firefox',
    label: 'آیفون · Firefox',
    env: {os: 'ios', browser: 'firefox', mobile: true},
  },
  {id: 'ipad-safari', label: 'آیپد · Safari', env: {os: 'ipados', browser: 'safari', mobile: true}},
  {
    id: 'android-chrome',
    label: 'اندروید · Chrome',
    env: {os: 'android', browser: 'chrome', mobile: true},
  },
  {
    id: 'android-samsung',
    label: 'اندروید · Samsung Internet',
    env: {os: 'android', browser: 'samsung', mobile: true},
  },
  {
    id: 'android-firefox',
    label: 'اندروید · Firefox',
    env: {os: 'android', browser: 'firefox', mobile: true},
  },
  {
    id: 'android-edge',
    label: 'اندروید · Edge',
    env: {os: 'android', browser: 'edge', mobile: true},
  },
  {
    id: 'android-opera',
    label: 'اندروید · Opera',
    env: {os: 'android', browser: 'opera', mobile: true},
  },
  {
    id: 'windows-chrome',
    label: 'رایانه · Chrome',
    env: {os: 'windows', browser: 'chrome', mobile: false},
  },
  {
    id: 'windows-edge',
    label: 'رایانه · Edge',
    env: {os: 'windows', browser: 'edge', mobile: false},
  },
  {id: 'mac-safari', label: 'مک · Safari', env: {os: 'macos', browser: 'safari', mobile: false}},
  {id: 'mac-chrome', label: 'مک · Chrome', env: {os: 'macos', browser: 'chrome', mobile: false}},
  {
    id: 'desktop-firefox',
    label: 'رایانه · Firefox',
    env: {os: 'windows', browser: 'firefox', mobile: false},
  },
]
